import { ArrowLeft, Printer } from 'lucide-react';
import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router';
import { type ReportView, displayPin } from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { Button } from '#client/components/ui/Button';
import { Page } from '#client/components/ui/Page';
import { RecordPage } from '#client/components/ui/RecordPage';
import { useCan } from '#client/hooks/useCan';
import { useResourceItem } from '#client/hooks/useResourceItem';
import { useRecordRecent } from '#client/hooks/useRecordRecent';
import { RESOURCE, routes } from '#client/lib/routes';
import { useAuth } from '#client/providers/AuthProvider';
import { CultureResultsCards } from './CultureResultsCard';
import { EntryActionBar } from './EntryActionBar';
import { ReportHistoryCard } from './ReportHistoryCard';
import {
  ConsultantCard,
  EntryIdentity,
  EntryNotes,
  EntryStatusBanner,
  ResultsCard,
} from './ReportEntrySections';
import { useResultEntry } from './useResultEntry';

const PATH = RESOURCE.reports;

export function ReportEntryPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, loading, error } = useResourceItem<ReportView>(PATH, id);

  const back = <Button icon={<ArrowLeft />} aria-label="Back" onClick={() => void navigate(-1)} />;

  return (
    <RecordPage
      title="Report"
      backAction={back}
      record={data}
      loading={loading}
      error={error}
      missingMessage="Report not found"
      fullWidth
    >
      {/*
        Keyed on the report id so switching reports remounts with fresh state
        rather than syncing props into state through an effect.
      */}
      {(report) => <ReportEntryForm key={report.id} report={report} />}
    </RecordPage>
  );
}

/**
 * Result entry.
 *
 * ## Expandable rows
 *
 * The common case is one line per parameter: name, a result field, the unit,
 * the reference range, and a flag if the value is outside it. Everything else —
 * choosing which cohort range applies, seeing the recorded range, the parameter
 * code for cross-checking against an analyser printout — lives in an expansion.
 *
 * The range picker in particular is not a `<select>` on every row: that would
 * be the single biggest source of visual noise on the screen while being
 * needed on perhaps one row in twenty.
 *
 * ## Keyboard first
 *
 * This is a screen someone works down with both hands on the keyboard while
 * reading off an analyser. Enter moves to the next result rather than
 * submitting the form — saving and navigating away mid-entry would be a
 * genuine data-loss bug on the one screen built for fast typing.
 *
 * ## No autosave
 *
 * Deliberately. The whole category treats result entry as explicit, auditable
 * state transitions, and a report is locked once completed. Silently persisting
 * half-typed values into a clinical record is the wrong default.
 *
 * The state, counters, payloads and keyboard rules live in `useResultEntry`;
 * the grid and the sticky bar are their own components. What is left here is
 * the page's shape.
 */
function ReportEntryForm({ report }: { report: ReportView }) {
  const navigate = useNavigate();
  const can = useCan();
  const { user } = useAuth();
  useRecordRecent(user?.id, {
    href: routes.report(report.id),
    title: `${report.name} — ${report.patient.name}`,
    subtitle: displayPin(report.patient.pin_no),
  });

  const isCompleted = report.report_status === 'completed';
  const canEdit = can('report.enterResults') && !isCompleted;

  const handleSaved = useCallback(
    () => void navigate(routes.patient(report.patient_id)),
    [navigate, report.patient_id],
  );
  const entry = useResultEntry(report, handleSaved);

  return (
    <Page
      title={report.name}
      fullWidth
      backAction={
        <Button
          icon={<ArrowLeft />}
          aria-label="Back to patient"
          onClick={() => void navigate(routes.patient(report.patient_id))}
        />
      }
      subtitle={<EntryIdentity patient={report.patient} />}
      secondaryActions={
        <Button
          icon={<Printer />}
          onClick={() => void navigate(routes.patientPrint(report.patient_id, report.id))}
        >
          Print
        </Button>
      }
    >
      {entry.form.formError && <Banner tone="critical">{entry.form.formError}</Banner>}

      <EntryStatusBanner report={report} canEdit={canEdit} canReopen={can('report.reopen')} />

      <form onSubmit={entry.form.handleSubmit(entry.draftPayload)} noValidate>
        <div className="c-form-stack">
          {report.culture && entry.culture ? (
            <CultureResultsCards
              stored={report.culture}
              draft={entry.culture}
              onChange={(update) => entry.setCulture((current) => current && update(current))}
              readOnly={!canEdit}
            />
          ) : (
            <ResultsCard reportName={report.name} entry={entry} canEdit={canEdit} />
          )}

          <ConsultantCard
            consultantId={entry.consultantId}
            onChange={entry.setConsultantId}
            canEdit={canEdit}
          />

          {/* Between Results and the notes. It fetches its own data and holds
              its own state: which visits print is a *print* choice, and
              folding it into `useResultEntry` would imply it gets saved with
              the report. */}
          {!report.culture && (
            <ReportHistoryCard reportId={report.id} patientId={report.patient_id} />
          )}

          <EntryNotes report={report} notes={entry.notes} canEdit={canEdit} />
        </div>

        <EntryActionBar
          entered={entry.entered}
          total={entry.total}
          allEntered={entry.allEntered}
          isCompleted={isCompleted}
          canEdit={canEdit}
          canReopen={can('report.reopen')}
          submitting={entry.form.submitting}
          saveRef={entry.saveRef}
          onComplete={entry.form.handleSubmit(entry.completePayload)}
          onReopen={entry.form.handleSubmit(entry.reopenPayload)}
        />
      </form>
    </Page>
  );
}
